<template>

  <article
    class="event-card"
    :class="{
      ended: event.isEnded,
      own: event.isOurEvent
    }"
    @click="openEventPage"
  >

    <!-- =====================================================
         IMAGE
    ====================================================== -->

    <div class="event-image-wrapper">

      <img
        v-if="event.imageURL"
        :src="event.imageURL"
        :alt="event.title || 'Ивент'"
        class="event-image"
      />


      <div
        v-else
        class="event-image-placeholder"
      >

        <svg
          viewBox="0 0 24 24"
          fill="none"
        >
          <rect
            x="4"
            y="5"
            width="16"
            height="15"
            rx="3"
            stroke="currentColor"
            stroke-width="1.7"
          />

          <path
            d="M8 3V7"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M16 3V7"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M4 9H20"
            stroke="currentColor"
            stroke-width="1.7"
          />
        </svg>

      </div>


      <!-- IMAGE GRADIENT -->

      <div class="image-gradient"></div>


      <!-- ENDED -->

      <div
        v-if="event.isEnded"
        class="ended-badge"
      >
        Завершён
      </div>


      <!-- OUR EVENT -->

      <div
        v-else-if="event.isOurEvent"
        class="our-event-badge"
      >

        <svg
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M5 12.5L9.5 17L19 7.5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        Ваш ивент

      </div>


      <!-- DATE -->

      <div class="date-badge">

        <template v-if="parsedEventDate">

          <strong>
            {{ eventDay }}
          </strong>

          <span>
            {{ eventMonth }}
          </span>

        </template>


        <template v-else>

          <span class="date-text">
            {{ event.dateOfEvent }}
          </span>

        </template>

      </div>


      <!-- CITY -->

      <div
        v-if="regionName"
        class="image-city"
      >

        <svg
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <circle
            cx="12"
            cy="10"
            r="2"
            stroke="currentColor"
            stroke-width="1.6"
          />
        </svg>

        {{ regionName }}

      </div>

    </div>


    <!-- =====================================================
         BODY
    ====================================================== -->

    <div class="event-body">

      <!-- COMPANY -->

      <div
        v-if="event.companyName"
        class="company-row"
      >

        <div class="company-logo">

          <img
            v-if="event.companyLogoURL"
            :src="event.companyLogoURL"
            :alt="event.companyName"
          />

          <span v-else>
            {{ companyInitial }}
          </span>

        </div>


        <div class="company-info">

          <span class="company-caption">
            Организатор
          </span>

          <strong>
            {{ event.companyName }}
          </strong>

        </div>


        <div class="company-arrow">

          <svg
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M7 5L12 10L7 15"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

        </div>

      </div>


      <!-- TITLE -->

      <div class="event-heading">

        <span class="event-type">
          Ивент
        </span>

        <h2>
          {{ event.title || "Без названия" }}
        </h2>

      </div>


      <!-- DESCRIPTION -->

      <p class="event-description">
        {{
          event.description
          || "Описание события отсутствует."
        }}
      </p>


      <!-- COMPANY DESCRIPTION -->

      <div
        v-if="
          event.companyAbout
          && !event.isOurEvent
        "
        class="company-about"
      >
        {{ event.companyAbout }}
      </div>


      <!-- ===================================================
           PARTICIPANTS
      ==================================================== -->

      <div class="event-bottom">

        <div
          v-if="event.participants?.length"
          class="participants"
        >

          <div class="avatars">

            <div
              v-for="(participant, index) in visibleParticipants"
              :key="
                participant.user_id
                || index
              "
              class="avatar-wrapper"
              :style="{
                zIndex:
                  visibleParticipants.length - index
              }"
            >

              <img
                v-if="participant.image_url"
                :src="participant.image_url"
                class="avatar"
                alt=""
              />

              <span v-else>
                ?
              </span>

            </div>

          </div>


          <div class="participants-text">

            <strong>
              {{ event.participants.length }}
            </strong>

            <span>
              {{ participantsTitle }}
            </span>

          </div>

        </div>


        <div
          v-else
          class="participants empty"
        >

          <div class="empty-participant-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="8"
                r="3"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <path
                d="M6 19C6 15.7 8.7 13 12 13C15.3 13 18 15.7 18 19"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <span>
            Пока никто не отметился
          </span>

        </div>


        <!-- CTA -->

        <button
          type="button"
          class="event-button"
          @click.stop="handleAction"
        >

          <span>
            {{
              event.isOurEvent
                ? "Редактировать"
                : "Подробнее"
            }}
          </span>


          <svg
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M7 5L12 10L7 15"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

        </button>

      </div>

    </div>

  </article>

</template>


<script setup>

import {
  computed
} from "vue";

import {
  useRouter
} from "vue-router";

import RegionService
  from "../utils/RegionService.js";


const router =
  useRouter();


const props =
  defineProps({
    event: {
      type: Object,
      required: true
    }
  });


/* ============================================================
   REGION
============================================================ */

const regionName =
  computed(() => {

    if (
      props.event.cityID === null
      || props.event.cityID === undefined
      || props.event.cityID === ""
    ) {
      return "";
    }


    return (
      RegionService.handleRegion(
        Number(
          props.event.cityID
        )
      )
      || ""
    );

  });


/* ============================================================
   DATE
============================================================ */

const parsedEventDate =
  computed(() => {

    const value =
      props.event.dateOfEvent;


    if (
      value === null
      || value === undefined
      || value === ""
    ) {
      return null;
    }


    const numeric =
      Number(value);


    if (
      !Number.isNaN(numeric)
    ) {

      const milliseconds =
        numeric < 100000000000
          ? numeric * 1000
          : numeric;


      const date =
        new Date(
          milliseconds
        );


      if (
        !Number.isNaN(
          date.getTime()
        )
      ) {
        return date;
      }

    }


    const date =
      new Date(value);


    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return null;
    }


    return date;

  });


const eventDay =
  computed(() => {

    if (
      !parsedEventDate.value
    ) {
      return "";
    }


    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        day: "2-digit"
      }
    ).format(
      parsedEventDate.value
    );

  });


const eventMonth =
  computed(() => {

    if (
      !parsedEventDate.value
    ) {
      return "";
    }


    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        month: "short"
      }
    )
      .format(
        parsedEventDate.value
      )
      .replace(".", "")
      .toUpperCase();

  });


/* ============================================================
   COMPANY
============================================================ */

const companyInitial =
  computed(() => {

    const name =
      props.event.companyName;


    if (!name) {
      return "?";
    }


    return name
      .trim()
      .charAt(0)
      .toUpperCase();

  });


/* ============================================================
   PARTICIPANTS
============================================================ */

const visibleParticipants =
  computed(() => {

    if (
      !Array.isArray(
        props.event.participants
      )
    ) {
      return [];
    }


    return props.event.participants
      .slice(
        0,
        3
      );

  });


const participantsTitle =
  computed(() => {

    const count =
      props.event.participants?.length
      || 0;


    const mod10 =
      count % 10;

    const mod100 =
      count % 100;


    if (
      mod10 === 1
      && mod100 !== 11
    ) {
      return "участник";
    }


    if (
      mod10 >= 2
      && mod10 <= 4
      && (
        mod100 < 12
        || mod100 > 14
      )
    ) {
      return "участника";
    }


    return "участников";

  });


/* ============================================================
   NAVIGATION
============================================================ */

function openEventPage() {

  if (
    props.event.isOurEvent
  ) {

    router.push({
      name:
        "EditEvent",

      params: {
        id:
          props.event.id
      }
    });

    return;
  }


  router.push({
    name:
      "event",

    params: {
      eventId:
        props.event.id
    },

    state: {
      event:
        props.event
    }
  });

}


function handleAction() {

  /*
   * Для своего ивента сохраняем
   * старую логику — редактирование.
   */

  if (
    props.event.isOurEvent
  ) {

    openEventPage();

    return;
  }


  /*
   * Для остальных сначала открываем
   * внутреннюю страницу ивента.
   */

  openEventPage();

}

</script>


<style scoped>

* {
  box-sizing: border-box;
}


/* ============================================================
   CARD
============================================================ */

.event-card {
  min-width: 0;

  overflow: hidden;

  background:
    rgba(
      255,
      255,
      255,
      0.94
    );

  border:
    1px solid
    rgba(
      30,
      43,
      61,
      0.07
    );

  border-radius: 22px;

  box-shadow:
    0 10px 30px
    rgba(
      15,
      23,
      42,
      0.05
    );

  cursor: pointer;

  transition:
    transform
    0.22s ease,
    border-color
    0.22s ease,
    box-shadow
    0.22s ease;
}


.event-card:hover {
  transform:
    translateY(-4px);

  border-color:
    rgba(
      52,
      127,
      231,
      0.16
    );

  box-shadow:
    0 20px 44px
    rgba(
      37,
      49,
      76,
      0.11
    );
}


.event-card.ended {
  opacity: 0.68;
}


.event-card.own {
  border-color:
    rgba(
      124,
      92,
      231,
      0.13
    );
}


/* ============================================================
   IMAGE
============================================================ */

.event-image-wrapper {
  position: relative;

  width: 100%;
  height: 260px;

  overflow: hidden;

  background: #eceff4;
}


.event-image {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transition:
    transform
    0.4s ease;
}


.event-card:hover
.event-image {
  transform:
    scale(1.025);
}


.event-image-placeholder {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #abb1bb;

  background:
    linear-gradient(
      135deg,
      #eef1f5,
      #e4e8ef
    );
}


.event-image-placeholder svg {
  width: 46px;
  height: 46px;
}


.image-gradient {
  position: absolute;

  inset: 0;

  pointer-events: none;

  background:
    linear-gradient(
      to top,
      rgba(
        7,
        10,
        18,
        0.32
      ),
      transparent
      55%
    );
}


/* ============================================================
   BADGES
============================================================ */

.ended-badge,
.our-event-badge {
  position: absolute;

  top: 15px;
  left: 15px;

  z-index: 2;

  min-height: 31px;

  display: inline-flex;
  align-items: center;

  gap: 6px;

  padding:
    0
    11px;

  color: #ffffff;

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.15
    );

  border-radius: 10px;

  backdrop-filter:
    blur(13px);

  -webkit-backdrop-filter:
    blur(13px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.ended-badge {
  background:
    rgba(
      39,
      43,
      50,
      0.82
    );
}


.our-event-badge {
  background:
    rgba(
      103,
      79,
      207,
      0.88
    );
}


.our-event-badge svg {
  width: 13px;
  height: 13px;
}


/* ============================================================
   DATE
============================================================ */

.date-badge {
  position: absolute;

  top: 15px;
  right: 15px;

  z-index: 2;

  min-width: 54px;
  height: 54px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding:
    0
    8px;

  color: #24282f;

  background:
    rgba(
      255,
      255,
      255,
      0.94
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.58
    );

  border-radius: 15px;

  box-shadow:
    0 8px 20px
    rgba(
      0,
      0,
      0,
      0.13
    );

  backdrop-filter:
    blur(12px);

  -webkit-backdrop-filter:
    blur(12px);
}


.date-badge strong {
  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
  line-height: 1;
}


.date-badge > span:not(.date-text) {
  margin-top: 3px;

  color: #777e88;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing: 0.05em;
}


.date-text {
  max-width: 85px;

  color: #555c65;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  line-height: 1.3;

  text-align: center;
}


/* ============================================================
   CITY
============================================================ */

.image-city {
  position: absolute;

  left: 15px;
  bottom: 15px;

  z-index: 2;

  max-width:
    calc(
      100% - 30px
    );

  display: inline-flex;
  align-items: center;

  gap: 5px;

  padding:
    7px
    9px;

  overflow: hidden;

  color: #ffffff;

  background:
    rgba(
      17,
      22,
      31,
      0.55
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.13
    );

  border-radius: 9px;

  backdrop-filter:
    blur(12px);

  -webkit-backdrop-filter:
    blur(12px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  text-overflow: ellipsis;
  white-space: nowrap;
}


.image-city svg {
  width: 12px;
  height: 12px;

  flex-shrink: 0;
}


/* ============================================================
   BODY
============================================================ */

.event-body {
  padding:
    18px
    19px
    18px;
}


/* ============================================================
   COMPANY
============================================================ */

.company-row {
  display: flex;
  align-items: center;

  gap: 10px;

  margin-bottom: 16px;

  padding:
    10px
    11px;

  background: #f7f8fa;

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.055
    );

  border-radius: 13px;
}


.company-logo {
  width: 37px;
  height: 37px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  color: #647da3;

  background:
    linear-gradient(
      135deg,
      #e8eef7,
      #f0f3f7
    );

  border-radius: 10px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 13px;
}


.company-logo img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.company-info {
  min-width: 0;

  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 2px;
}


.company-caption {
  color: #a0a6af;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing: 0.06em;

  text-transform: uppercase;
}


.company-info strong {
  overflow: hidden;

  color: #3c4149;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  text-overflow: ellipsis;
  white-space: nowrap;
}


.company-arrow {
  width: 27px;
  height: 27px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #a1a7b0;

  background: #ffffff;

  border-radius: 8px;

  transition:
    color
    0.18s ease,
    transform
    0.18s ease;
}


.company-arrow svg {
  width: 13px;
  height: 13px;
}


.event-card:hover
.company-arrow {
  color: #347fe7;

  transform:
    translateX(2px);
}


/* ============================================================
   TITLE
============================================================ */

.event-type {
  display: block;

  margin-bottom: 5px;

  color: #979da6;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.event-heading h2 {
  margin: 0;

  color: #282c33;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 19px;
  line-height: 1.28;
}


/* ============================================================
   DESCRIPTION
============================================================ */

.event-description {
  display: -webkit-box;

  margin:
    10px
    0
    0;

  overflow: hidden;

  color: #6f7681;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;

  line-height: 1.55;

  -webkit-box-orient:
    vertical;

  -webkit-line-clamp:
    4;
}


.company-about {
  display: -webkit-box;

  margin-top: 13px;

  padding:
    10px
    11px;

  overflow: hidden;

  color: #848b95;

  background:
    rgba(
      115,
      125,
      140,
      0.055
    );

  border-radius: 10px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  line-height: 1.5;

  -webkit-box-orient:
    vertical;

  -webkit-line-clamp:
    2;
}


/* ============================================================
   BOTTOM
============================================================ */

.event-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 14px;

  margin-top: 18px;

  padding-top: 15px;

  border-top:
    1px solid
    rgba(
      31,
      44,
      62,
      0.06
    );
}


/* ============================================================
   PARTICIPANTS
============================================================ */

.participants {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 9px;
}


.avatars {
  display: flex;

  padding-left: 2px;
}


.avatar-wrapper {
  position: relative;

  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  margin-left: -7px;

  color: #87909b;

  background: #e8ebef;

  border:
    2px solid
    #ffffff;

  border-radius: 50%;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 8px;
}


.avatar-wrapper:first-child {
  margin-left: 0;
}


.avatar {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.participants-text {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 1px;
}


.participants-text strong {
  color: #505761;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 9px;
}


.participants-text span {
  color: #989ea7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


.participants.empty {
  color: #9aa1aa;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


.empty-participant-icon {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #9da4ae;

  background: #f1f3f6;

  border-radius: 8px;
}


.empty-participant-icon svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   BUTTON
============================================================ */

.event-button {
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding:
    0
    13px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #2887f7,
      #486de6
    );

  border: none;
  border-radius: 11px;

  box-shadow:
    0 7px 16px
    rgba(
      40,
      135,
      247,
      0.17
    );

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  transition:
    transform
    0.18s ease,
    box-shadow
    0.18s ease;
}


.event-button:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 10px 21px
    rgba(
      40,
      135,
      247,
      0.25
    );
}


.event-button svg {
  width: 13px;
  height: 13px;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.event-card {
  background: #232428;

  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );

  box-shadow:
    0 12px 30px
    rgba(
      0,
      0,
      0,
      0.2
    );
}


html[data-theme="dark"]
.event-card:hover {
  border-color:
    rgba(
      68,
      132,
      231,
      0.17
    );

  box-shadow:
    0 20px 46px
    rgba(
      0,
      0,
      0,
      0.32
    );
}


html[data-theme="dark"]
.event-image-wrapper {
  background: #2d2f34;
}


html[data-theme="dark"]
.event-image-placeholder {
  color: #676e78;

  background:
    linear-gradient(
      135deg,
      #292b30,
      #33363c
    );
}


html[data-theme="dark"]
.company-row {
  background: #2d2f34;

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.company-logo {
  color: #a6b2c4;

  background:
    linear-gradient(
      135deg,
      #32353b,
      #393c43
    );
}


html[data-theme="dark"]
.company-info strong {
  color: #e4e5e8;
}


html[data-theme="dark"]
.company-arrow {
  color: #858c96;

  background: #34363c;
}


html[data-theme="dark"]
.event-heading h2 {
  color: #f0f1f3;
}


html[data-theme="dark"]
.event-description {
  color: #a1a6ae;
}


html[data-theme="dark"]
.company-about {
  color: #949aa4;

  background:
    rgba(
      255,
      255,
      255,
      0.035
    );
}


html[data-theme="dark"]
.event-bottom {
  border-top-color:
    rgba(
      255,
      255,
      255,
      0.065
    );
}


html[data-theme="dark"]
.avatar-wrapper {
  color: #a1a7b0;

  background: #393b41;

  border-color: #232428;
}


html[data-theme="dark"]
.participants-text strong {
  color: #d5d7da;
}


html[data-theme="dark"]
.empty-participant-icon {
  color: #9299a3;

  background: #303238;
}


/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 600px
) {

  .event-card {
    border-radius: 18px;
  }


  .event-image-wrapper {
    height: 210px;
  }


  .event-body {
    padding:
      16px
      15px
      15px;
  }


  .event-heading h2 {
    font-size: 17px;
  }


  .event-bottom {
    flex-direction: column;

    align-items: stretch;
  }


  .event-button {
    width: 100%;
  }


  .participants {
    min-height: 32px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion:
  reduce
) {

  .event-card,
  .event-image,
  .event-button,
  .company-arrow {
    transition: none;
  }

}

</style>